import React, { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from '../firebase/config';
import { showToast } from '../utils/toast';
import { Power, PowerOff, PlusCircle, AlertTriangle, CheckCircle, Clock, Search, RefreshCw, Package, Zap, Trash2, LoaderCircle } from 'lucide-react';
import AddTenantModal from '../components/AddTenantModal';
import MetricCard from '../components/MetricCard';
import ConfirmationModal from '../components/ConfirmationModal';

const BlockingOverlay = () => (
    <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        color: 'white'
    }}>
        <LoaderCircle size={48} className="animate-spin" />
        <h4 className="mt-3">Testando conexão...</h4>
        <p>Por favor, aguarde. Não feche esta página.</p>
    </div>
);


function DashboardPage({ user, mspData }) {
    const [tenants, setTenants] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [validatingId, setValidatingId] = useState(null);
    const [isBlockingInteraction, setIsBlockingInteraction] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalProps, setConfirmModalProps] = useState({});
    const [isReconsentingId, setIsReconsentingId] = useState(null);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };

        if (isBlockingInteraction) {
            window.addEventListener('beforeunload', handleBeforeUnload);
        }

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isBlockingInteraction]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const status = params.get('consent_status');
        const message = params.get('message');

        if (status && message) {
            const icon = status === 'success' ? 'success' : 'error';
            const title = status === 'success' ? 'Operação Concluída!' : 'Ocorreu um Erro';
            showToast(icon, `${title} ${message}`);
            window.history.replaceState(null, '', window.location.pathname);
        }
    }, []);

    useEffect(() => {
        const tenantsRef = collection(db, 'msp_users', user.uid, 'tenants');
        const unsubscribe = onSnapshot(tenantsRef, (querySnapshot) => {
            const tenantsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTenants(tenantsData);
            setIsLoading(false);
        }, (error) => {
            console.error("Erro ao buscar tenants:", error);
            showToast('error', 'Não foi possível carregar os tenants.');
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [user.uid]);

    const handleToggleTenant = (tenantId) => {
        const tenant = tenants.find(t => t.id === tenantId);
        if (!tenant) return;

        const updateAction = async () => {
            setShowConfirmModal(false); 
            try {
                const functions = getFunctions();
                const toggleTenantStatus = httpsCallable(functions, 'toggle_tenant_status');
                const result = await toggleTenantStatus({ tenantId: tenantId });
                showToast('success', result.data.message);
            } catch (error) {
                console.error("Erro ao alterar status do tenant:", error);
                showToast('error', `Falha ao alterar status: ${error.message}`);
            }
        };

        if (tenant.isEnabled) {
            setConfirmModalProps({
                title: 'Tem certeza?',
                body: "Você está prestes a desabilitar este tenant.",
                confirmText: 'Sim, desabilitar!',
                confirmVariant: 'danger',
                onConfirm: updateAction,
            });
            setShowConfirmModal(true);
        } else {
            updateAction();
        }
    };

    const handleValidateConnection = async (tenantId) => {
        setValidatingId(tenantId);
        setIsBlockingInteraction(true); 

        setTimeout(async () => {
            try {
                const functions = getFunctions();
                const validateFunction = httpsCallable(functions, 'validate_tenant_connection');
                const result = await validateFunction({ tenantDocId: tenantId });
                showToast('success', result.data.message);
            } catch (error) {
                console.error("Erro ao tentar validar conexão:", error);
                showToast('error', `Falha ao validar a conexão: ${error.message}`);
            } finally {
                setValidatingId(null);
                setIsBlockingInteraction(false); 
            }
        }, 50); 
    };

    const handleDeleteTenant = (tenant) => {
        if (!tenant) return;

        const performDelete = async () => {
            setShowConfirmModal(false);
            try {
                const functions = getFunctions();
                const deleteTenantFunc = httpsCallable(functions, 'delete_tenant');
                await deleteTenantFunc({ tenantDocId: tenant.id });
                showToast('success', `Tenant "${tenant.displayName}" foi excluído.`);
            } catch (error) {
                console.error("Erro ao excluir tenant:", error);
                showToast('error', `Falha ao excluir tenant: ${error.message}`);
            }
        };

        setConfirmModalProps({
            title: `Excluir Tenant?`,
            body: `Você tem certeza que deseja excluir o tenant "${tenant.displayName}"? Esta ação não pode ser desfeita.`,
            confirmText: 'Sim, excluir!',
            confirmVariant: 'danger',
            onConfirm: performDelete,
        });
        setShowConfirmModal(true);
    };

    const handleReconsent = async (tenant) => {
        if (!tenant) return;
        
        setIsReconsentingId(tenant.id); 

        try {
            const functions = getFunctions();
            const getLink = httpsCallable(functions, 'generate_reconsent_link');
            const result = await getLink({ tenantDocId: tenant.id });
            
            const consentUrl = result.data.consentUrl;
            
            window.location.href = consentUrl;

        } catch (error) {
            console.error("Erro ao gerar link de re-consentimento:", error);
            showToast('error', `Falha ao iniciar atualização: ${error.message}`);
            setIsReconsentingId(null); 
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'active': return <CheckCircle className="me-2 text-success" size={18} />;
            case 'awaiting_consent': return <Clock className="me-2 text-info" size={18} />;
            case 'pending_validation': return <Zap className="me-2 text-primary" size={18} />;
            case 'error_limit_reached':
            case 'error_duplicate':
            case 'error_connection_failed':
                return <AlertTriangle className="me-2 text-danger" size={18} />;
            default: return <Clock className="me-2 text-secondary" size={18} />;
        }
    };

    const authenticatedTenantCount = tenants.filter(tenant => tenant.status !== 'awaiting_consent').length;
    const tenantLimit = mspData?.maxTenants;
    const isLimitReached = tenantLimit !== undefined && authenticatedTenantCount >= tenantLimit;
    const buttonTitle = isLimitReached ? `Limite de ${tenantLimit} tenants autenticáveis atingido. Para aumentar, contate o suporte.` : 'Cadastrar Tenant';
    const activeTenants = tenants.filter(t => t.status === 'active').length;
    const pendingTenants = tenants.filter(t => ['awaiting_consent', 'pending_validation'].includes(t.status)).length;
    const errorTenants = tenants.filter(t => t.status.startsWith('error')).length;
    const slotsAvailable = tenantLimit - authenticatedTenantCount;
    const filteredTenants = tenants.filter(tenant =>
        tenant.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            {isBlockingInteraction && <BlockingOverlay />}
            <AddTenantModal show={showModal} onClose={() => setShowModal(false)} />
            <ConfirmationModal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} {...confirmModalProps} />

            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1 className="h3 mb-0">Dashboard</h1>
                <button
                    onClick={() => setShowModal(true)}
                    className="btn btn-primary d-flex align-items-center"
                    disabled={isLimitReached}
                    title={buttonTitle}
                >
                    <PlusCircle size={16} className="me-2" />
                    Cadastrar Tenant
                </button>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-lg-3 col-md-6"><MetricCard title="Tenants Ativos" value={activeTenants} icon={<CheckCircle />} variant="success" /></div>
                <div className="col-lg-3 col-md-6"><MetricCard title="Aguardando Ação" value={pendingTenants} icon={<Clock />} variant="info" /></div>
                <div className="col-lg-3 col-md-6"><MetricCard title="Com Erro" value={errorTenants} icon={<AlertTriangle />} variant="danger" /></div>
                <div className="col-lg-3 col-md-6"><MetricCard title="Vagas no Plano" value={slotsAvailable < 0 ? 0 : slotsAvailable} icon={<Package />} variant="primary" /></div>
            </div>

            <div className="card shadow-sm">
                <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h5 className="card-title mb-0">Gerenciamento de Tenants</h5>
                    <div className="input-group" style={{maxWidth: '300px'}}>
                        <span className="input-group-text"><Search size={16}/></span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Filtrar por nome..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th scope="col" className="ps-4">Nome do Tenant</th>
                                    <th scope="col">Status</th>
                                    <th scope="col" className="text-end pe-3">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan="3" className="text-center p-5">Carregando...</td></tr>
                                ) : (
                                    filteredTenants.map(tenant => (
                                        <tr key={tenant.id}>
                                            <td className="ps-4 align-middle fw-medium">{tenant.displayName}</td>
                                            <td className="align-middle">
                                                <div className="d-flex align-items-center">
                                                    {getStatusIcon(tenant.status)}
                                                    <span className={`text-capitalize status-badge status-${tenant.status}`}>
                                                        {tenant.status?.replace(/_/g, ' ')}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="align-middle text-end pe-3">
                                                <div className="d-flex justify-content-end gap-2">
                                                    {(tenant.status === 'pending_validation' || tenant.status === 'error_connection_failed') && (
                                                        <button
                                                            onClick={() => handleValidateConnection(tenant.id)}
                                                            className="btn btn-sm btn-primary"
                                                            disabled={validatingId === tenant.id}
                                                        >
                                                            {validatingId === tenant.id ? 'Testando...' : 'Testar Conexão'}
                                                        </button>
                                                    )}

                                                    {tenant.status === 'active' && (
                                                        <button
                                                            onClick={() => handleToggleTenant(tenant.id)}
                                                            className={`btn btn-sm ${tenant.isEnabled ? 'btn-outline-secondary' : 'btn-success'}`}
                                                        >
                                                            {tenant.isEnabled ? 'Desabilitar' : 'Habilitar'}
                                                        </button>
                                                    )}

                                                    {tenant.status === 'active' && (
                                                        <button
                                                            onClick={() => handleReconsent(tenant)}
                                                            className="btn btn-sm btn-outline-primary d-flex align-items-center"
                                                            title="Atualizar Permissões (Re-autorizar)"
                                                            disabled={isReconsentingId === tenant.id}
                                                        >
                                                            {isReconsentingId === tenant.id 
                                                                ? <LoaderCircle size={16} className="animate-spin" />
                                                                : <RefreshCw size={16} />
                                                            }
                                                            <span className="ms-1">Atualizar Permissões</span>
                                                        </button>
                                                    )}

                                                    {tenant.status !== 'active' && (
                                                        <button
                                                            onClick={() => handleDeleteTenant(tenant)}
                                                            className="btn btn-sm btn-outline-danger"
                                                        >
                                                            Excluir
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                {!isLoading && filteredTenants.length === 0 && (
                                    <tr><td colSpan="3" className="text-center p-5">Nenhum tenant encontrado.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </>
    );
}

export default DashboardPage;